/**
 * User Management API Functions
 * Handles user invitation, member list retrieval and deletion functionality
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../../auth/authUtils';
import { getApiGateway } from '../../api/endpointUtils';
import type {
  InviteMemberParams,
  InviteMemberResponse,
  FleetUserType,
  LoginApp,
  GetInviteMemberListParams,
  GetInviteMemberListResponse,
  DeleteMemberParams,
  DeleteMemberResponse,
} from '../types';

import { 
  INVITE_MEMBER_ERROR_MESSAGES,
  GET_INVITE_MEMBER_ERROR_MESSAGES,
  DELETE_MEMBER_ERROR_MESSAGES 
} from '../types';

/**
 * Invite a member to the fleet with specified role
 * Sends invitation email and initiates registration process (3-day validity)
 * 
 * @param params - Invitation parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with invitation response
 */
export async function inviteMember(params: Omit<InviteMemberParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<InviteMemberResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: InviteMemberParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      loginApp: 'fleeta',
      ...params
    };

    // Validate required parameters
    if (!requestParams.guestEmail) {
      throw new Error('Guest email is required');
    }
    
    if (!requestParams.userType) {
      throw new Error('User type is required');
    }
    
    if (!['SubMaster', 'User'].includes(requestParams.userType)) {
      throw new Error('Invalid user type. Must be "SubMaster" or "User"');
    }

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/InviteMember`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: InviteMemberResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = INVITE_MEMBER_ERROR_MESSAGES[data.resultcode as keyof typeof INVITE_MEMBER_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Invite a SubMaster to the fleet
 * Convenience function for inviting SubMaster role
 * 
 * @param guestEmail - Email address to invite as SubMaster
 * @param loginApp - Login application (default: 'fleeta')
 * @returns Promise with invitation response
 */
export async function inviteSubMaster(
  guestEmail: string, 
  loginApp: LoginApp = 'fleeta'
): Promise<InviteMemberResponse> {
  return inviteMember({
    guestEmail,
    userType: 'SubMaster',
    loginApp
  });
}

/**
 * Invite a User to the fleet
 * Convenience function for inviting User role
 * 
 * @param guestEmail - Email address to invite as User
 * @param loginApp - Login application (default: 'fleeta')
 * @returns Promise with invitation response
 */
export async function inviteUser(
  guestEmail: string, 
  loginApp: LoginApp = 'fleeta'
): Promise<InviteMemberResponse> {
  return inviteMember({
    guestEmail,
    userType: 'User',
    loginApp
  });
}

/**
 * Get list of invited members for the fleet
 * Retrieves information about all invited members with optional filtering and pagination
 * 
 * @param params - Query parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with invited member list response
 */
export async function getInviteMemberList(params: Omit<GetInviteMemberListParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
} = { searchKey: '' }): Promise<GetInviteMemberListResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetInviteMemberListParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params,
      searchKey: params.searchKey || '' // Use params.searchKey or default to empty string
    };

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('email', requestParams.email);
    queryParams.append('user_token', requestParams.user_token);
    queryParams.append('tokenType', requestParams.tokenType || 'web');
    
    if (requestParams.startIndex && requestParams.startIndex >= 1) {
      queryParams.append('startIndex', requestParams.startIndex.toString());
    }
    if (requestParams.endIndex && requestParams.endIndex >= 1) {
      queryParams.append('endIndex', requestParams.endIndex.toString());
    }
    if (requestParams.ordering !== undefined) {
      queryParams.append('ordering', requestParams.ordering.toString());
    }
    // searchKey is required - always include it (empty string if no search)
    queryParams.append('searchKey', requestParams.searchKey);

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/UserInviteMemberInfo?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetInviteMemberListResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_INVITE_MEMBER_ERROR_MESSAGES[data.resultcode as keyof typeof GET_INVITE_MEMBER_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Delete a member from the fleet
 * Removes member account and associated data based on user type
 * 
 * @param params - Deletion parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with deletion response
 */
export async function deleteMember(params: Omit<DeleteMemberParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<DeleteMemberResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: DeleteMemberParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      loginApp: 'fleeta',
      ...params
    };

    // Validate required parameters
    if (!requestParams.deleteEmail) {
      throw new Error('Delete email is required');
    }
    
    if (!requestParams.deleteEmailUserType) {
      throw new Error('Delete email user type is required');
    }
    
    if (!['SubMaster', 'User'].includes(requestParams.deleteEmailUserType)) {
      throw new Error('Invalid delete email user type. Must be "SubMaster" or "User"');
    }

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/UserInviteMemberDelete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: DeleteMemberResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = DELETE_MEMBER_ERROR_MESSAGES[data.resultcode as keyof typeof DELETE_MEMBER_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}